import { useCallback, useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft, faCheck, faDownload, faEye, faFileInvoiceDollar,
    faMagnifyingGlass, faPen, faPlus, faRotate, faTrash, faUpload, faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';

import {
    createInvoiceDraft, downloadInvoiceCsv, fetchInvoice, fetchInvoiceCoverage, fetchInvoiceCustomers,
    fetchInvoiceAccountCodes, downloadPallettrackInvoiceTemplate,
    issueInvoice, markInvoicePaid, openInvoicePdf, searchInvoices,
    updateInvoiceDraft, uploadPallettrackInvoiceCsv, voidInvoice,
} from '../api/invoices';
import { useAlert } from '../providers/AlertProvider';
import { useAuthProvider } from '../providers/AuthProvider';
import { useTheme } from './themeControl/ThemeProvider';
import { ActivityIndicator } from './custom/ActivityIndicator';
import './css/InvoicesPage.css';


const isoDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
const today = isoDate(new Date());
const monthAgo = new Date();
monthAgo.setDate(monthAgo.getDate() - 30);
const thirtyDaysAgo = isoDate(monthAgo);
const emptyLine = () => ({ item_type: 'bespoke', service_date: today, description: '', reference: '', quantity: 1, unit_price: '', vat_rate: '' });
const emptyDraft = () => ({
    customer_id: '', account_code_id: '', issue_date: today, due_date: '', notes: '', route_ids: [], route_items: [], items: [],
});

const money = (value, currency = 'GBP') => new Intl.NumberFormat('en-GB', {
    style: 'currency', currency,
}).format(Number(value || 0));
const displayDate = (value) => {
    if (!value) return '—';
    const [datePart] = String(value).split('T');
    const [year, month, day] = datePart.split('-');
    return year && month && day ? `${day}/${month}/${year}` : value;
};
const surchargeText = (value) => Array.isArray(value) ? value.join(' ') : (value || '—');
const dueDateForTerms = (issueDate, paymentTerms) => {
    if (!issueDate) return '';
    const [year, month, day] = issueDate.split('-').map(Number);
    if (!year || !month || !day) return '';
    const dueDate = new Date(year, month - 1, day);
    const days = { one_week: 7, two_weeks: 14, sixty_days: 60, ninety_days: 90 }[paymentTerms];
    if (days) dueDate.setDate(dueDate.getDate() + days);
    else {
        const targetMonth = new Date(year, month, 1);
        const lastDay = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0).getDate();
        dueDate.setFullYear(targetMonth.getFullYear(), targetMonth.getMonth(), Math.min(day, lastDay));
    }
    return isoDate(dueDate);
};
const routeDescription = (route) => {
    const collection = [route.collection_name, route.collection_address, route.collection_postcode].filter(Boolean).join(', ');
    const delivery = [route.delivery_name, route.delivery_address, route.delivery_postcode].filter(Boolean).join(', ');
    if (collection && delivery) return `${collection} to ${delivery}`;
    return collection || delivery || route.route || 'Transport service';
};

const InvoiceStatus = ({ status }) => <span className={`invoice-status invoice-status-${status}`}>{status}</span>;

const InvoicesPage = () => {
    const { theme } = useTheme();
    const { showAlert } = useAlert();
    const { isDev, isManager, permissions, isLoading: authLoading } = useAuthProvider();
    const navigate = useNavigate();
    const canView = isDev || isManager || permissions.perm_invoice_view || permissions.perm_invoice_edit;
    const canEdit = isDev || isManager || permissions.perm_invoice_edit;

    const [view, setView] = useState('register');
    const [loading, setLoading] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [accountCodes, setAccountCodes] = useState([]);
    const [coverage, setCoverage] = useState({ summary: {}, routes: [] });
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [draft, setDraft] = useState(emptyDraft());
    const [editingId, setEditingId] = useState(null);
    const [filters, setFilters] = useState({ search: '', status: '', from_date: '', to_date: '' });
    const [coverageFilters, setCoverageFilters] = useState({ from_date: thirtyDaysAgo, to_date: today, customer_id: '' });
    const [coverageState, setCoverageState] = useState('all');
    const [showExport, setShowExport] = useState(false);
    const [exportStep, setExportStep] = useState('dates');
    const [exporting, setExporting] = useState(false);
    const [exportFilters, setExportFilters] = useState({ from_date: thirtyDaysAgo, to_date: today, format: 'xero' });
    const [showUpload, setShowUpload] = useState(false);
    const [uploadStep, setUploadStep] = useState('file');
    const [uploading, setUploading] = useState(false);
    const [uploadData, setUploadData] = useState({ file: null, customer_id: '', account_code_id: '' });

    const notifyError = useCallback((message) => {
        showAlert({ message, type: 'danger', buttons: [{ label: 'Close' }] });
    }, [showAlert]);

    const loadInvoices = useCallback(async (nextFilters = filters) => {
        setLoading(true);
        const response = await searchInvoices(nextFilters);
        if (response.status === 200) setInvoices(response.data.invoices || []);
        else notifyError(response.data?.error || 'Unable to load invoices.');
        setLoading(false);
    }, [filters, notifyError]);

    const loadCoverage = useCallback(async (nextFilters = coverageFilters) => {
        setLoading(true);
        const response = await fetchInvoiceCoverage(nextFilters);
        if (response.status === 200) setCoverage(response.data);
        else notifyError(response.data?.error || 'Unable to load route coverage.');
        setLoading(false);
        return response;
    }, [coverageFilters, notifyError]);

    useEffect(() => {
        if (!authLoading && !canView) navigate('/');
    }, [authLoading, canView, navigate]);

    useEffect(() => {
        if (!canView) return;
        Promise.all([fetchInvoiceCustomers(), fetchInvoiceAccountCodes(true)]).then(([customerResponse, codeResponse]) => {
            if (customerResponse.status === 200) setCustomers(customerResponse.data.customers || []);
            else notifyError(customerResponse.data?.error || 'Unable to load invoice customers.');
            if (codeResponse.status === 200) setAccountCodes(codeResponse.data.account_codes || []);
            else notifyError(codeResponse.data?.error || 'Unable to load invoice account codes.');
        });
        loadInvoices();
    }, [canView]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const customer = customers.find(option => option.id === Number(draft.customer_id));
        if (!customer || !draft.issue_date) return;
        const dueDate = dueDateForTerms(draft.issue_date, customer.payment_terms);
        setDraft(current => current.due_date === dueDate ? current : { ...current, due_date: dueDate });
    }, [customers, draft.customer_id, draft.issue_date]);

    const openRegister = () => {
        setView('register');
        setSelectedInvoice(null);
        setEditingId(null);
        loadInvoices();
    };

    const openCoverage = () => {
        setView('coverage');
        loadCoverage();
    };

    const openNewDraft = () => {
        setDraft(emptyDraft());
        setEditingId(null);
        setCoverage({ summary: {}, routes: [] });
        setView('editor');
    };

    const viewInvoice = async (invoiceId) => {
        setLoading(true);
        const response = await fetchInvoice(invoiceId);
        if (response.status === 200) {
            setSelectedInvoice(response.data);
            setView('detail');
        } else notifyError(response.data?.error || 'Unable to load invoice.');
        setLoading(false);
    };

    const editInvoice = async (invoiceId) => {
        setLoading(true);
        const response = await fetchInvoice(invoiceId);
        if (response.status !== 200) {
            notifyError(response.data?.error || 'Unable to load invoice.');
            setLoading(false);
            return;
        }
        const invoice = response.data;
        const routeItems = (invoice.invoice_items || []).filter(item => item.source_route_id);
        setDraft({
            customer_id: invoice.customer_id,
            account_code_id: invoice.account_code_id || '',
            issue_date: invoice.issue_date || today,
            due_date: invoice.due_date || '',
            notes: invoice.notes || '',
            route_ids: routeItems.map(item => item.source_route_id),
            route_items: routeItems,
            items: (invoice.invoice_items || [])
                .filter(item => !item.source_route_id && item.item_type !== 'fuel_surcharge')
                .map(item => ({ ...item })),
        });
        setEditingId(invoiceId);
        setCoverageFilters(current => ({ ...current, customer_id: invoice.customer_id }));
        setCoverage({ summary: {}, routes: [] });
        setView('editor');
        setLoading(false);
    };

    const selectCustomer = async (customerId) => {
        const numericId = customerId ? Number(customerId) : '';
        setDraft(current => ({ ...current, customer_id: numericId, route_ids: [], route_items: [] }));
        if (numericId) {
            const next = { ...coverageFilters, customer_id: numericId };
            setCoverageFilters(next);
            await loadCoverage(next);
        } else setCoverage({ summary: {}, routes: [] });
    };

    const selectAccountCode = (accountCodeId) => {
        const selectedCode = accountCodes.find(
            accountCode => accountCode.id === Number(accountCodeId)
        );
        setDraft(current => {
            const compatibleRouteIds = new Set(
                (coverage.routes || [])
                    .filter(route => Boolean(route.is_network_freight) === Boolean(selectedCode?.is_pallet_network))
                    .map(route => route.route_id)
            );
            return {
                ...current,
                account_code_id: accountCodeId,
                route_ids: current.route_ids.filter(routeId => compatibleRouteIds.has(routeId)),
                route_items: current.route_items.filter(item => compatibleRouteIds.has(item.source_route_id)),
            };
        });
    };

    const toggleRoute = (route) => {
        setDraft(current => {
            const selected = current.route_ids.includes(route.route_id);
            return {
                ...current,
                route_ids: selected ? current.route_ids.filter(id => id !== route.route_id) : [...current.route_ids, route.route_id],
                route_items: selected
                    ? current.route_items.filter(item => item.source_route_id !== route.route_id)
                    : [...current.route_items, {
                        source_route_id: route.route_id,
                        service_date: route.service_date,
                        description: routeDescription(route),
                        job_comments: route.job_comments || '',
                        reference: route.account_ref || '',
                        quantity: 1,
                        unit_price: route.costings || 0,
                        vat_rate: '',
                    }],
            };
        });
    };

    const updateRouteLine = (routeId, key, value) => setDraft(current => ({
        ...current,
        route_items: current.route_items.map(item => item.source_route_id === routeId ? { ...item, [key]: value } : item),
    }));
    const updateLine = (index, key, value) => setDraft(current => ({
        ...current, items: current.items.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item),
    }));

    const saveDraft = async () => {
        if (!draft.customer_id) return notifyError('Select an invoice customer.');
        if (!draft.account_code_id) return notifyError('Select an invoice account code.');
        if (!draft.route_ids.length && !draft.items.length) return notifyError('Add at least one route or bespoke line.');
        if (draft.items.some(item => !item.description.trim() || item.unit_price === '')) return notifyError('Every bespoke line needs a description and unit price.');
        const payload = {
            ...draft,
            items: draft.items.map(item => ({ ...item, vat_rate: item.vat_rate === '' ? undefined : item.vat_rate })),
            route_items: draft.route_items.map(item => ({ ...item, vat_rate: item.vat_rate === '' ? undefined : item.vat_rate })),
        };
        setLoading(true);
        const response = editingId ? await updateInvoiceDraft(editingId, payload) : await createInvoiceDraft(payload);
        if (response.status === 200 || response.status === 201) {
            showAlert({ message: editingId ? 'Draft updated.' : 'Draft created.', type: 'success', buttons: [{ label: 'Close' }] });
            const invoiceId = editingId || response.data.invoice_id;
            await viewInvoice(invoiceId);
        } else notifyError(response.data?.error || 'Unable to save invoice draft.');
        setLoading(false);
    };

    const runAction = (label, action, invoiceId) => showAlert({
        message: `${label} this invoice?`, type: 'warning',
        buttons: [
            { label: 'Cancel', type: 'secondary' },
            { label, type: 'primary', onClick: async () => {
                setLoading(true);
                const response = await action(invoiceId);
                if (response.status === 200) {
                    const successMessage = label === 'Issue' && response.data?.email_sent
                        ? 'Invoice issued and emailed to the customer successfully.'
                        : `Invoice ${label.toLowerCase()} successfully.`;
                    showAlert({ message: successMessage, type: 'success', buttons: [{ label: 'Close' }] });
                    if (view === 'detail') await viewInvoice(invoiceId); else await loadInvoices();
                } else notifyError(response.data?.error || `Unable to ${label.toLowerCase()} invoice.`);
                setLoading(false);
            } },
        ],
    });

    const pdfAction = async (invoiceId, download) => {
        const response = await openInvoicePdf(invoiceId, download);
        if (response.status !== 200) notifyError(response.data?.error || 'Unable to open invoice PDF.');
    };

    const openExport = () => {
        setExportStep('dates');
        setExportFilters({ from_date: thirtyDaysAgo, to_date: today, format: 'xero' });
        setShowExport(true);
    };

    const continueExport = () => {
        if (!exportFilters.from_date || !exportFilters.to_date) return notifyError('Select both dates for the export.');
        if (exportFilters.from_date > exportFilters.to_date) return notifyError('The from date cannot be after the to date.');
        setExportStep('format');
    };

    const runCsvExport = async () => {
        setExporting(true);
        const response = await downloadInvoiceCsv(exportFilters);
        if (response.status === 200) setShowExport(false);
        else notifyError(response.data?.error || 'Unable to download invoice CSV.');
        setExporting(false);
    };

    const openUpload = () => {
        setUploadStep('file');
        setUploadData({ file: null, customer_id: '', account_code_id: '' });
        setShowUpload(true);
    };

    const continueUpload = () => {
        if (!uploadData.file) return notifyError('Select a Pallet-Track invoice CSV.');
        if (!uploadData.file.name.toLowerCase().endsWith('.csv')) return notifyError('Only CSV files can be uploaded.');
        setUploadStep('pricing');
    };

    const downloadUploadTemplate = async () => {
        const response = await downloadPallettrackInvoiceTemplate();
        if (response.status !== 200) {
            notifyError(response.data?.error || 'Unable to download the CSV template.');
        }
    };

    const runCsvUpload = async () => {
        if (!uploadData.customer_id) return notifyError('Select an invoice customer.');
        if (!uploadData.account_code_id) return notifyError('Select a pallet-network account code.');
        setUploading(true);
        const response = await uploadPallettrackInvoiceCsv(
            uploadData.file, uploadData.customer_id, uploadData.account_code_id
        );
        setUploading(false);
        if (response.status === 201) {
            setShowUpload(false);
            showAlert({ message: `${response.data.imported_count} Pallet-Track lines imported into a draft invoice.`, type: 'success', buttons: [{ label: 'Close' }] });
            await viewInvoice(response.data.invoice_id);
        } else notifyError(response.data?.error || 'Unable to import the invoice CSV.');
    };

    const selectedAccountCode = accountCodes.find(
        accountCode => accountCode.id === Number(draft.account_code_id)
    );
    const availableRoutes = (coverage.routes || []).filter(route =>
        (route.coverage_status === 'uninvoiced' || route.invoice_id === editingId)
        && selectedAccountCode
        && Boolean(route.is_network_freight) === Boolean(selectedAccountCode.is_pallet_network)
    );
    const visibleCoverage = (coverage.routes || []).filter(route => coverageState === 'all' || route.coverage_status === coverageState);
    const selectedCustomer = customers.find(customer => customer.id === Number(draft.customer_id));

    if (authLoading || !canView) return null;
    return (
        <div id={theme} className="invoice-page">
            <header className="invoice-page-header">
                <div>
                    <h2><FontAwesomeIcon icon={faFileInvoiceDollar} style={{ width: '25px' }} /> Invoicing</h2>
                    <p>Route coverage, draft preparation and invoice lifecycle.</p>
                </div>
                <nav className="invoice-view-tabs" aria-label="Invoice views">
                    <button className={view === 'register' || view === 'detail' ? 'active' : ''} onClick={openRegister}>Register</button>
                    <button className={view === 'coverage' ? 'active' : ''} onClick={openCoverage}>Route coverage</button>
                    <Button size="sm" variant="outline-light" onClick={openExport}><FontAwesomeIcon icon={faDownload} style={{ width: '14px' }} /> Download CSV</Button>
                    {canEdit && <Button size="sm" variant="outline-light" onClick={openUpload}><FontAwesomeIcon icon={faUpload} style={{ width: '14px' }} /> Upload CSV</Button>}
                    {canEdit && <Button size="sm" onClick={openNewDraft}><FontAwesomeIcon icon={faPlus} style={{ width: '14px' }} /> New draft</Button>}
                </nav>
            </header>

            {loading && <div className="invoice-loading"><ActivityIndicator /></div>}

            {!loading && view === 'register' && (
                <main className="invoice-workspace">
                    <div className="invoice-filter-bar">
                        <label><span>Search</span><input value={filters.search} onChange={event => setFilters({ ...filters, search: event.target.value })} placeholder="Number, customer or account" /></label>
                        <label><span>Status</span><select value={filters.status} onChange={event => setFilters({ ...filters, status: event.target.value })}><option value="">All statuses</option><option value="draft">Draft</option><option value="issued">Issued</option><option value="paid">Paid</option><option value="void">Void</option></select></label>
                        <label><span>From</span><input type="date" lang="en-GB" value={filters.from_date} onChange={event => setFilters({ ...filters, from_date: event.target.value })} /></label>
                        <label><span>To</span><input type="date" lang="en-GB" value={filters.to_date} onChange={event => setFilters({ ...filters, to_date: event.target.value })} /></label>
                        <Button onClick={() => loadInvoices()}><FontAwesomeIcon icon={faMagnifyingGlass} style={{ width: '14px' }} /> Search</Button>
                    </div>
                    <div className="invoice-table-wrap">
                        <table className="invoice-table">
                            <thead><tr><th>Invoice</th><th>Customer</th><th>Account code</th><th>Issue</th><th>Due</th><th>Status</th><th className="amount">Total</th><th className="invoice-actions-cell">Actions</th></tr></thead>
                            <tbody>{invoices.map(invoice => <tr key={invoice.id}>
                                <td className="invoice-number">{invoice.invoice_number || `Draft #${invoice.id}`}</td>
                                <td>{invoice.customer_name}<small>{invoice.account_ref || ''}</small></td>
                                <td>{invoice.account_code || '—'}<small>{invoice.account_code_name || ''}</small></td>
                                <td>{displayDate(invoice.issue_date)}</td><td>{displayDate(invoice.due_date)}</td>
                                <td><InvoiceStatus status={invoice.status} /></td>
                                <td className="amount">{money(invoice.total, invoice.currency)}</td>
                                <td className="invoice-actions-cell"><div className="invoice-actions">
                                    <button title="View" onClick={() => viewInvoice(invoice.id)}><FontAwesomeIcon icon={faEye} style={{ width: '14px' }} /></button>
                                    {canEdit && invoice.status === 'draft' && <button title="Edit" onClick={() => editInvoice(invoice.id)}><FontAwesomeIcon icon={faPen} style={{ width: '14px' }} /></button>}
                                    <button title="Preview PDF" onClick={() => pdfAction(invoice.id, false)}><FontAwesomeIcon icon={faFileInvoiceDollar} style={{ width: '14px' }} /></button>
                                </div></td>
                            </tr>)}</tbody>
                        </table>
                        {!invoices.length && <div className="invoice-empty">No invoices match these filters.</div>}
                    </div>
                </main>
            )}

            {!loading && view === 'coverage' && (
                <main className="invoice-workspace">
                    <div className="invoice-metrics">
                        {['total', 'planned', 'exception', 'uninvoiced', 'reserved', 'invoiced'].map(key => <button key={key} onClick={() => setCoverageState(key === 'total' ? 'all' : key)} className={coverageState === (key === 'total' ? 'all' : key) ? 'active' : ''}><span>{key}</span><strong>{coverage.summary?.[key] || 0}</strong></button>)}
                    </div>
                    <div className="invoice-filter-bar">
                        <label><span>Invoice customer</span><select value={coverageFilters.customer_id} onChange={event => setCoverageFilters({ ...coverageFilters, customer_id: event.target.value })}><option value="">All customers</option>{customers.map(customer => <option key={customer.id} value={customer.id}>{customer.company}</option>)}</select></label>
                        <label><span>From</span><input type="date" lang="en-GB" value={coverageFilters.from_date} onChange={event => setCoverageFilters({ ...coverageFilters, from_date: event.target.value })} /></label>
                        <label><span>To</span><input type="date" lang="en-GB" value={coverageFilters.to_date} onChange={event => setCoverageFilters({ ...coverageFilters, to_date: event.target.value })} /></label>
                        <Button onClick={() => loadCoverage()}><FontAwesomeIcon icon={faRotate} style={{ width: '14px' }} /> Refresh</Button>
                    </div>
                    <div className="invoice-table-wrap"><table className="invoice-table"><thead><tr><th>Date</th><th>Route / job</th><th>Job comments</th><th>Account</th><th>Status</th><th className="amount">Costing</th><th>Invoice</th></tr></thead><tbody>{visibleCoverage.map(route => <tr key={route.route_id}><td>{displayDate(route.service_date)}</td><td>{route.job || route.route || `Route #${route.route_id}`}</td><td>{route.job_comments || '—'}</td><td>{route.account_ref || '—'}</td><td><InvoiceStatus status={route.coverage_status} />{route.exception_reason && <small>{route.exception_reason}</small>}</td><td className="amount">{route.costings === null || route.costings === '' ? '—' : money(route.costings)}</td><td>{route.invoice_number || '—'}</td></tr>)}</tbody></table>{!visibleCoverage.length && <div className="invoice-empty">No routes match this view.</div>}</div>
                </main>
            )}

            {!loading && view === 'editor' && (
                <main className="invoice-workspace invoice-editor">
                    <button className="invoice-back" onClick={openRegister}><FontAwesomeIcon icon={faArrowLeft} style={{ width: '14px' }} /> Back to register</button>
                    <div className="invoice-editor-heading"><div><span>{editingId ? `Draft #${editingId}` : 'New invoice'}</span><h3>{editingId ? 'Edit draft' : 'Prepare draft'}</h3></div><Button onClick={saveDraft}>{editingId ? 'Save changes' : 'Create draft'}</Button></div>
                    <section className="invoice-form-band">
                        <label><span>Invoice customer</span><select value={draft.customer_id} onChange={event => selectCustomer(event.target.value)}><option value="">Select customer</option>{customers.map(customer => <option key={customer.id} value={customer.id}>{customer.company}</option>)}</select></label>
                        <label><span>Revenue account code</span><select value={draft.account_code_id} onChange={event => selectAccountCode(event.target.value)}><option value="">Select account code</option>{accountCodes.filter(accountCode => accountCode.is_active || accountCode.id === Number(draft.account_code_id)).map(accountCode => <option key={accountCode.id} value={accountCode.id} disabled={!accountCode.is_active}>{accountCode.code} — {accountCode.name}{accountCode.is_pallet_network ? ' · Pallet network' : ''}{accountCode.is_active ? '' : ' (archived)'}</option>)}</select></label>
                        <label><span>Issue date</span><input type="date" lang="en-GB" value={draft.issue_date} onChange={event => setDraft({ ...draft, issue_date: event.target.value })} /></label>
                        <label><span>Due date</span><input type="date" lang="en-GB" value={draft.due_date} readOnly /></label>
                        <label className="wide"><span>Notes</span><input value={draft.notes} onChange={event => setDraft({ ...draft, notes: event.target.value })} placeholder="Optional invoice notes" /></label>
                    </section>
                    {selectedCustomer && <div className="invoice-bill-to"><strong>Bill to:</strong> {selectedCustomer.company} · {selectedCustomer.address || 'No address'} · {selectedCustomer.postcode || 'No postcode'}</div>}
                    {!editingId && <section className="invoice-editor-section">
                        <div className="section-title"><div><h4>Invoice-ready routes</h4><p>Select uninvoiced routes from locked days for this billing customer.</p></div><span>{draft.route_ids.length} selected</span></div>
                        {!draft.customer_id ? <div className="invoice-empty compact">Select a customer to load routes.</div> : !selectedAccountCode ? <div className="invoice-empty compact">Select a revenue account code to show compatible routes.</div> : <div className="invoice-route-list">{availableRoutes.map(route => <label key={route.route_id} className={draft.route_ids.includes(route.route_id) ? 'selected' : ''}><input type="checkbox" checked={draft.route_ids.includes(route.route_id)} onChange={() => toggleRoute(route)} /><span><strong>{route.job || route.route || `Route #${route.route_id}`}</strong><small>{displayDate(route.service_date)} · {route.account_ref || 'No reference'}{route.is_network_freight ? ' · Network Freight' : ''}</small></span><b>{money(route.costings)}</b></label>)}{!availableRoutes.length && <div className="invoice-empty compact">No compatible routes from locked days in this date range.</div>}</div>}
                    </section>}
                    {!!draft.route_items.length && <section className="invoice-editor-section"><div className="section-title"><div><h4>Route invoice lines</h4><p>Adjust invoice wording, comments or charge; the server recalculates totals.</p></div></div><div className="invoice-line-grid invoice-line-head"><span>Description</span><span>Job comments</span><span>Reference</span><span>Unit price</span><span>VAT %</span><span></span></div>{draft.route_items.map(item => <div className="invoice-line-grid" key={item.source_route_id}><input value={item.description || ''} onChange={event => updateRouteLine(item.source_route_id, 'description', event.target.value)} /><input value={item.job_comments || ''} onChange={event => updateRouteLine(item.source_route_id, 'job_comments', event.target.value)} /><input value={item.reference || ''} onChange={event => updateRouteLine(item.source_route_id, 'reference', event.target.value)} /><input type="number" min="0" step="0.01" value={item.unit_price ?? ''} onChange={event => updateRouteLine(item.source_route_id, 'unit_price', event.target.value)} /><input type="number" min="0" max="100" step="0.01" value={item.vat_rate ?? ''} placeholder="Default" onChange={event => updateRouteLine(item.source_route_id, 'vat_rate', event.target.value)} /><span></span></div>)}</section>}
                    <section className="invoice-editor-section"><div className="section-title"><div><h4>Bespoke lines</h4><p>Add non-route services, surcharges or adjustments.</p></div><Button variant="outline-primary" size="sm" onClick={() => setDraft(current => ({ ...current, items: [...current.items, emptyLine()] }))}><FontAwesomeIcon icon={faPlus} style={{ width: '14px' }} /> Add line</Button></div><div className="invoice-line-grid invoice-line-head"><span>Description</span><span>Reference</span><span>Qty / price / VAT</span><span>Service date</span><span></span></div>{draft.items.map((item, index) => <div className="invoice-line-grid" key={index}><input value={item.description} placeholder="Description" onChange={event => updateLine(index, 'description', event.target.value)} /><input value={item.reference || ''} placeholder="Reference" onChange={event => updateLine(index, 'reference', event.target.value)} /><div className="invoice-number-fields"><input aria-label="Quantity" type="number" min="0.001" step="0.001" value={item.quantity} onChange={event => updateLine(index, 'quantity', event.target.value)} /><input aria-label="Unit price" type="number" min="0" step="0.01" value={item.unit_price} placeholder="Price" onChange={event => updateLine(index, 'unit_price', event.target.value)} /><input aria-label="VAT rate" type="number" min="0" max="100" step="0.01" value={item.vat_rate} placeholder="VAT" onChange={event => updateLine(index, 'vat_rate', event.target.value)} /></div><input type="date" lang="en-GB" value={item.service_date || ''} onChange={event => updateLine(index, 'service_date', event.target.value)} /><button className="remove-line" title="Remove line" onClick={() => setDraft(current => ({ ...current, items: current.items.filter((_, itemIndex) => itemIndex !== index) }))}><FontAwesomeIcon icon={faTrash} style={{ width: '14px' }} /></button></div>)}</section>
                </main>
            )}

            {!loading && view === 'detail' && selectedInvoice && (
                <main className="invoice-workspace invoice-detail">
                    <button className="invoice-back" onClick={openRegister}><FontAwesomeIcon icon={faArrowLeft} style={{ width: '14px' }} /> Back to register</button>
                    <div className="invoice-detail-heading"><div><span>Invoice</span><h3>{selectedInvoice.invoice_number || `Draft #${selectedInvoice.id}`}</h3><p>{selectedInvoice.customer_name} · {selectedInvoice.account_ref || 'No account reference'}</p></div><InvoiceStatus status={selectedInvoice.status} /></div>
                    <div className="invoice-detail-actions"><Button variant="outline-primary" onClick={() => pdfAction(selectedInvoice.id, false)}><FontAwesomeIcon icon={faEye} style={{ width: '14px' }} /> Preview PDF</Button><Button variant="outline-primary" onClick={() => pdfAction(selectedInvoice.id, true)}><FontAwesomeIcon icon={faDownload} style={{ width: '14px' }} /> Download</Button>{canEdit && selectedInvoice.status === 'draft' && <><Button onClick={() => editInvoice(selectedInvoice.id)}><FontAwesomeIcon icon={faPen} style={{ width: '14px' }} /> Edit</Button><Button variant="success" onClick={() => runAction('Issue', issueInvoice, selectedInvoice.id)}><FontAwesomeIcon icon={faCheck} style={{ width: '14px' }} /> Issue &amp; email</Button></>}{canEdit && selectedInvoice.status === 'issued' && <Button variant="success" onClick={() => runAction('Mark paid', markInvoicePaid, selectedInvoice.id)}><FontAwesomeIcon icon={faCheck} style={{ width: '14px' }} /> Mark paid</Button>}{canEdit && ['draft', 'issued'].includes(selectedInvoice.status) && <Button variant="outline-danger" onClick={() => runAction('Void', voidInvoice, selectedInvoice.id)}><FontAwesomeIcon icon={faXmark} style={{ width: '14px' }} /> Void</Button>}</div>
                    <section className="invoice-detail-meta"><div><span>Issue date</span><strong>{displayDate(selectedInvoice.issue_date)}</strong></div><div><span>Due date</span><strong>{displayDate(selectedInvoice.due_date)}</strong></div><div><span>Revenue account code</span><strong>{selectedInvoice.account_code ? `${selectedInvoice.account_code} — ${selectedInvoice.account_code_name || ''}` : '—'}</strong></div><div><span>Billing address</span><strong>{selectedInvoice.billing_address || '—'} {selectedInvoice.billing_postcode || ''}</strong></div><div><span>Billing email</span><strong>{selectedInvoice.billing_email || '—'}</strong></div></section>
                    <div className="invoice-table-wrap"><table className="invoice-table">
                        {selectedInvoice.is_pallet_network ? <>
                            <thead><tr><th>Consignment</th><th>Delivery</th><th>POD</th><th>Reference</th><th>Consignee</th><th>Destination</th><th>Postcode</th><th className="amount">Kg</th><th>Service</th><th>Surcharges</th><th className="amount">Pallets</th><th>O/S</th><th className="amount">Price</th></tr></thead>
                            <tbody>{(selectedInvoice.invoice_items || []).map(item => <tr key={item.id} className={item.is_oversize ? 'invoice-row-oversize' : ''}><td>{item.consignment_number || '—'}</td><td>{displayDate(item.consignment_delivery_date || item.service_date)}</td><td>{displayDate(item.pod_date)}</td><td>{item.customer_reference_1 || item.reference || '—'}<small>{item.customer_reference_2 || ''}</small></td><td>{item.consignee || '—'}</td><td>{item.destination || '—'}</td><td>{item.network_delivery_postcode || '—'}</td><td className="amount">{item.weight_kg ?? '—'}</td><td>{item.service_code || '—'}</td><td>{surchargeText(item.network_surcharges)}</td><td className="amount">{item.pallet_count ?? '—'}</td><td>{item.is_oversize ? 'Yes' : '—'}</td><td className="amount">{money(item.net_amount, selectedInvoice.currency)}</td></tr>)}</tbody>
                        </> : <>
                            <thead><tr><th>Date</th><th className="invoice-description-column">Description</th><th>Job comments</th><th>Reference</th><th className="amount">Qty</th><th className="amount">Unit</th><th className="amount">VAT</th><th className="amount">Gross</th></tr></thead>
                            <tbody>{(selectedInvoice.invoice_items || []).map(item => <tr key={item.id}><td>{displayDate(item.service_date)}</td><td className="invoice-description-column">{item.description}<small>{item.item_type}</small></td><td>{item.job_comments || '—'}</td><td>{item.reference || '—'}</td><td className="amount">{item.quantity}</td><td className="amount">{money(item.unit_price, selectedInvoice.currency)}</td><td className="amount">{item.vat_rate}%</td><td className="amount">{money(item.gross_amount, selectedInvoice.currency)}</td></tr>)}</tbody>
                        </>}
                    </table></div>
                    <aside className="invoice-totals"><div><span>Subtotal</span><strong>{money(selectedInvoice.subtotal, selectedInvoice.currency)}</strong></div><div><span>VAT</span><strong>{money(selectedInvoice.vat_total, selectedInvoice.currency)}</strong></div><div className="grand"><span>Total</span><strong>{money(selectedInvoice.total, selectedInvoice.currency)}</strong></div></aside>
                    {selectedInvoice.notes && <div className="invoice-notes"><strong>Notes</strong><p>{selectedInvoice.notes}</p></div>}
                </main>
            )}

            <Modal show={showExport} onHide={() => !exporting && setShowExport(false)} centered className={`invoice-export-modal ${theme}`} backdropClassName="invoice-export-backdrop">
                <Modal.Header closeButton>
                    <Modal.Title>{exportStep === 'dates' ? 'Choose date range' : 'Choose CSV format'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {exportStep === 'dates' ? <>
                        <p>Invoices are selected by issue date. Draft and void invoices are not included.</p>
                        <div className="invoice-export-dates">
                            <label><span>From</span><input type="date" lang="en-GB" value={exportFilters.from_date} max={exportFilters.to_date} onChange={event => setExportFilters(current => ({ ...current, from_date: event.target.value }))} /></label>
                            <label><span>To</span><input type="date" lang="en-GB" value={exportFilters.to_date} min={exportFilters.from_date} onChange={event => setExportFilters(current => ({ ...current, to_date: event.target.value }))} /></label>
                        </div>
                    </> : <>
                        <p>Select the finance application format for this export.</p>
                        <label className="invoice-export-format selected">
                            <input type="radio" name="invoice-export-format" value="xero" checked={exportFilters.format === 'xero'} onChange={event => setExportFilters(current => ({ ...current, format: event.target.value }))} />
                            <span><strong>Xero</strong><small>Sales invoice CSV</small></span>
                        </label>
                    </>}
                </Modal.Body>
                <Modal.Footer>
                    {exportStep === 'format' && <Button variant="outline-secondary" disabled={exporting} onClick={() => setExportStep('dates')}>Back</Button>}
                    <Button variant="secondary" disabled={exporting} onClick={() => setShowExport(false)}>Cancel</Button>
                    {exportStep === 'dates'
                        ? <Button onClick={continueExport}>Continue</Button>
                        : <Button disabled={exporting} onClick={runCsvExport}><FontAwesomeIcon icon={faDownload} style={{ width: '14px' }} /> {exporting ? 'Downloading...' : 'Download CSV'}</Button>}
                </Modal.Footer>
            </Modal>
            <Modal show={showUpload} onHide={() => !uploading && setShowUpload(false)} centered className={`invoice-export-modal ${theme}`} backdropClassName="invoice-export-backdrop">
                <Modal.Header closeButton>
                    <Modal.Title>{uploadStep === 'file' ? 'Upload Pallet-Track invoice' : 'Choose customer and account code'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {uploadStep === 'file' ? <>
                        <p>Select the Pallet-Track invoice CSV. The file is validated before a draft is created.</p>
                        <Button variant="outline-primary" size="sm" className="mb-3" onClick={downloadUploadTemplate}><FontAwesomeIcon icon={faDownload} style={{ width: '14px' }} /> Download compatible template</Button>
                        <input type="file" accept=".csv,text/csv" onChange={event => setUploadData(current => ({ ...current, file: event.target.files?.[0] || null }))} />
                    </> : <>
                        <p>Customer pallet, service and surcharge prices will be added to every imported CSV charge.</p>
                        <div className="invoice-upload-fields">
                            <label><span>Invoice customer</span><select value={uploadData.customer_id} onChange={event => setUploadData(current => ({ ...current, customer_id: event.target.value }))}><option value="">Select customer</option>{customers.map(customer => <option key={customer.id} value={customer.id}>{customer.company}</option>)}</select></label>
                            <label><span>Revenue account code</span><select value={uploadData.account_code_id} onChange={event => setUploadData(current => ({ ...current, account_code_id: event.target.value }))}><option value="">Select pallet-network account code</option>{accountCodes.filter(code => code.is_active && code.is_pallet_network).map(code => <option key={code.id} value={code.id}>{code.code} — {code.name}</option>)}</select></label>
                        </div>
                    </>}
                </Modal.Body>
                <Modal.Footer>
                    {uploadStep === 'pricing' && <Button variant="outline-secondary" disabled={uploading} onClick={() => setUploadStep('file')}>Back</Button>}
                    <Button variant="secondary" disabled={uploading} onClick={() => setShowUpload(false)}>Cancel</Button>
                    {uploadStep === 'file'
                        ? <Button onClick={continueUpload}>Continue</Button>
                        : <Button disabled={uploading} onClick={runCsvUpload}><FontAwesomeIcon icon={faUpload} style={{ width: '14px' }} /> {uploading ? 'Importing...' : 'Create draft'}</Button>}
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default InvoicesPage;